import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import SparkMD5 from 'spark-md5';
import { queryClient } from 'src/components/queryProvider';
import { IAsset } from 'src/interfaces';
import { getPreviewUrlSrc, getThumbnailSrc } from 'src/utils/gcf-helper';
import { queryKeys } from 'src/utils/queryKeys';

import { requestCCApi } from '../../../utils/axios-utils';
import {
  buildPlaceholderAsset,
  hexToBase64,
  isPlaceholderUrl,
  setUploadCacheBuster,
} from './helper';

// On image-selector unmount: free each placeholder's blob and swap the row to
// the real backend URL — kept in the list so the grid still shows it on reopen.
export const revokePlaceholderAssets = () => {
  const assets = queryClient.getQueryData<IAsset[]>(queryKeys.assets.all());
  const placeholders = assets?.filter((asset) =>
    isPlaceholderUrl(asset.thumbnailUrl),
  );
  if (!placeholders?.length) return;

  placeholders.forEach((asset) => URL.revokeObjectURL(asset.thumbnailUrl));

  queryClient.setQueryData<IAsset[]>(queryKeys.assets.all(), (old = []) =>
    old.map((asset) =>
      isPlaceholderUrl(asset.thumbnailUrl)
        ? {
            ...asset,
            previewUrl: getPreviewUrlSrc(asset.fileName),
            thumbnailUrl: getThumbnailSrc(asset.fileName),
          }
        : asset,
    ),
  );
};

export const useUploadAsset = () => {
  return useMutation({
    mutationFn: uploadAsset,
    onSettled: async (_data, _error, file: File) => {
      const uploadedFileName = file.name;
      const cacheBuster = Date.now();

      setUploadCacheBuster(uploadedFileName, cacheBuster);
      (window as any).__GCF_ASSET_REVISION__ = cacheBuster;
      new BroadcastChannel('gcf-asset-revision').postMessage(cacheBuster);

      if (_error) return;

      // The refetch returns the not-yet-indexed list and would drop every
      // upload placeholder. Keep the earlier ones (captured before the refetch)
      // and add this upload on top, then re-apply them over the fresh list.
      // Blobs are revoked on image-selector unmount (revokePlaceholderAssets).
      const existingPlaceholders = (
        queryClient.getQueryData<IAsset[]>(queryKeys.assets.all()) ?? []
      ).filter((asset) => isPlaceholderUrl(asset.thumbnailUrl));

      // Release the previous same-name placeholder's blob memory before
      // replacing it below (a re-upload of the same file).
      existingPlaceholders
        .filter((asset) => asset.fileName === file.name)
        .forEach((asset) => URL.revokeObjectURL(asset.thumbnailUrl));

      await queryClient.refetchQueries(queryKeys.assets.invalidateAll());

      const placeholders = [
        buildPlaceholderAsset(file),
        ...existingPlaceholders.filter((asset) => asset.fileName !== file.name),
      ];
      const placeholderNames = new Set(
        placeholders.map((asset) => asset.fileName),
      );
      queryClient.setQueryData<IAsset[]>(queryKeys.assets.all(), (old = []) => [
        ...placeholders,
        ...old.filter((asset) => !placeholderNames.has(asset.fileName)),
      ]);

      queryClient.invalidateQueries(queryKeys.products.invalidateAll());
      queryClient.invalidateQueries(queryKeys.ccAssets.invalidateAll());
    },
  });
};

export const uploadAsset = async (file: File) => {
  const url = `/hqAssets`;
  const CHUNK_SIZE = 1024 * 1024 * 10; // 10MB chunks for large files

  if (!file) {
    return;
  }
  // Validate filename before proceeding
  const filenameValidationResult = validateFilename(file.name);
  if (!filenameValidationResult.isValid) {
    throw new Error(`Invalid filename: ${filenameValidationResult.error}`);
  }

  const spark = new SparkMD5.ArrayBuffer();
  let offset = 0;
  // Read the file in chunks
  while (offset < file.size) {
    const slice = file.slice(offset, offset + CHUNK_SIZE);
    const arrayBuffer = await slice.arrayBuffer();
    spark.append(arrayBuffer);
    offset += CHUNK_SIZE;
  }

  // Generate the MD5 hash and Base64 encode it
  const md5Hash = spark.end(); // Finalize the MD5 hash

  // Convert the hash to Base64
  const base64Hash = hexToBase64(md5Hash);

  const headers = new Headers();
  headers.append('Content-Type', 'application/json');
  headers.append('Content-MD5', base64Hash);

  const response = await requestCCApi(
    {
      url,
      method: 'POST',
      data: {
        name: file.name,
        hash: md5Hash,
      },
    },
    true,
    'Failed to upload image',
  );

  if (response.uploadUrl) {
    const uploadResponse = await axios.put(response.uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
        'Content-MD5': base64Hash,
      },
    });

    return uploadResponse.data;
  }

  return [];
};

const FORBIDDEN_CHARS = /[$"'\\/()<>#?`*&|:;=+%@\\]/;
const EXTENSION_PATTERN = /\.[a-z0-9]+$/i;

const validateFilename = (
  filename: string,
): { isValid: boolean; error?: string } => {
  if (!filename || filename.trim().length === 0) {
    return { isValid: false, error: 'Filename cannot be empty' };
  }

  if (filename.length < 1 || filename.length > 128) {
    return {
      isValid: false,
      error: 'Filename must be between 1 and 128 characters',
    };
  }

  if (FORBIDDEN_CHARS.test(filename)) {
    return {
      isValid: false,
      error: 'Use letters, numbers, spaces, hyphens(-), or underscores(_)',
    };
  }

  if (!EXTENSION_PATTERN.test(filename)) {
    return {
      isValid: false,
      error: 'File extension should be lowercase (e.g., .jpg, .png, .pdf)',
    };
  }

  return { isValid: true };
};
